import React from "react";
import { ApiError, cockpit } from "@/api";
import type { AssigneeRef, RequestDetail } from "@/api";
import type { PushToast, ToastAction } from "@/notifications/toast-queue";
import { useT } from "../../i18n";
import { normalizeCloseReason } from "../screen-workspace.logic";
import { undoDoneRefusal, undoDoneRefusalKey } from "../request-done-undo";
import { requestNotAssignedRefusal } from "../request-not-assigned";
import { requestTrashRefusal, requestTrashRefusalKey } from "../request-trash";

type Act = (
  p: Promise<unknown>,
  okTitle: string,
  okMsg?: React.ReactNode | ((result: unknown) => React.ReactNode),
  undo?: ToastAction,
) => Promise<boolean>;

interface WorkspaceActionsOptions {
  request: RequestDetail["request"] | null;
  userId?: string;
  /** Suppress the "newly assigned" toast for a principal this session just added itself. */
  onLocalSelfAssign?: (requestId: string) => void;
  setData: React.Dispatch<React.SetStateAction<RequestDetail | null>>;
  pushToast: PushToast;
  onBack: () => void;
  onTrashed?: (requestId: string) => void;
  handleNotAssigned: (error: unknown) => boolean;
  act: Act;
}

export function useWorkspaceActions({
  request,
  userId,
  onLocalSelfAssign,
  setData,
  pushToast,
  onBack,
  onTrashed,
  handleNotAssigned,
  act,
}: WorkspaceActionsOptions) {
  const t = useT();
  const [closeOpen, setCloseOpen] = React.useState(false);
  const [closeReason, setCloseReason] = React.useState("");
  const [closeBusy, setCloseBusy] = React.useState(false);
  const [closeError, setCloseError] = React.useState<string | null>(null);
  const [trashOpen, setTrashOpen] = React.useState(false);
  const [trashBusy, setTrashBusy] = React.useState(false);
  const [trashError, setTrashError] = React.useState<string | null>(null);
  const assignees = request?.assignees ?? [];

  const openMarkDone = () => {
    setCloseReason("");
    setCloseError(null);
    setCloseOpen(true);
  };

  const markDoneUndo = (versionAtDone: number): ToastAction => ({
    label: t("toast.undo"),
    run: async () => {
      try {
        if (!request) return;
        await cockpit.undoDone(request.id, versionAtDone);
        pushToast({ tone: "success", title: t("ws.undoDoneToast") });
      } catch (error: any) {
        const refusal = undoDoneRefusal(error);
        if (refusal) {
          pushToast({ tone: "warning", title: t(undoDoneRefusalKey(refusal)) });
          return;
        }
        if (requestNotAssignedRefusal(error)) {
          pushToast({ tone: "warning", title: t("ws.undoDoneNotAssigned") });
          return;
        }
        pushToast({ tone: "danger", title: t("common.error"), msg: error?.message || String(error) });
      }
    },
  });

  const submitMarkDone = () => {
    if (!request) return;
    setCloseBusy(true);
    setCloseError(null);
    cockpit.closeRequest(request.id, normalizeCloseReason(closeReason) ?? undefined, request.version)
      .then((result) => {
        setData((current) => current
          ? {
              ...current,
              request: {
                ...current.request,
                status: "closed",
                lifecycle_status: "done",
                version: result.version,
              },
            }
          : current);
        setCloseBusy(false);
        setCloseOpen(false);
        pushToast({
          tone: "success",
          title: t("ws.markDoneToast"),
          undo: markDoneUndo(result.version),
        });
        onBack();
      })
      .catch((error: any) => {
        setCloseBusy(false);
        if (handleNotAssigned(error)) {
          setCloseOpen(false);
          setCloseError(null);
          return;
        }
        setCloseError(error instanceof ApiError && (error.data as { code?: string })?.code === "REQUEST_VERSION_CHANGED"
          ? t("ws.markDoneStale") : error?.message || t("ws.markDoneFailed"));
      });
  };

  // #1787: the trash. Moving takes the version the operator is looking at, so a request that moved
  // since is refused instead of set aside; the toast's undo and the banner's Restore both put it
  // back through the same versioned call.
  const openTrash = () => {
    setTrashError(null);
    setTrashOpen(true);
  };

  const restoreFromTrash = async (requestId: string, version: number): Promise<boolean> => {
    try {
      await cockpit.restoreRequest(requestId, version);
      pushToast({ tone: "success", title: t("ws.trash.restoredToast") });
      return true;
    } catch (error: any) {
      if (handleNotAssigned(error)) return false;
      const refusal = requestTrashRefusal(error);
      pushToast(refusal
        ? { tone: "warning", title: t(requestTrashRefusalKey(refusal)) }
        : { tone: "danger", title: t("common.error"), msg: error?.message || String(error) });
      return false;
    }
  };

  const submitTrash = () => {
    if (!request || typeof request.version !== "number") return;
    const requestId = request.id;
    setTrashBusy(true);
    setTrashError(null);
    cockpit.trashRequest(requestId, request.version)
      .then((result) => {
        setData((current) => current
          ? {
              ...current,
              request: {
                ...current.request,
                status: result.status,
                version: result.version,
                trashed_at: result.trashed_at,
                purge_at: result.purge_at,
              },
            }
          : current);
        setTrashBusy(false);
        setTrashOpen(false);
        pushToast({
          tone: "success",
          title: t("ws.trash.movedToast"),
          undo: {
            label: t("toast.undo"),
            run: async () => { await restoreFromTrash(requestId, result.version); },
          },
        });
        onTrashed?.(requestId);
        onBack();
      })
      .catch((error: any) => {
        setTrashBusy(false);
        if (handleNotAssigned(error)) {
          setTrashOpen(false);
          return;
        }
        const refusal = requestTrashRefusal(error);
        setTrashError(refusal ? t(requestTrashRefusalKey(refusal))
          : !(error instanceof ApiError) || error.status >= 500 ? t("inbox.bulk.unknownOutcome")
          : error.message || t("ws.trash.failed"));
      });
  };

  // #803: the set is edited one principal at a time, so the undo of each write is the opposite
  // write on that same principal — never a restore of the whole previous set, which would silently
  // discard what somebody else changed in between.
  // An admin who puts THEMSELVES on a request must not be told about it by a toast a moment
  // later: the poll that notices the change cannot tell their own click from a colleague's, so
  // the click says so locally. Every path that adds the acting user goes through here — the undo
  // of a removal included, which is where #536's regression came from.
  const addingSelf = (principal: AssigneeRef) => {
    if (request && principal.kind === "user" && principal.id === userId) {
      onLocalSelfAssign?.(request.id);
    }
  };
  const addAssignee = (principal: AssigneeRef) => {
    if (!request) return;
    addingSelf(principal);
    void act(cockpit.addRequestAssignee(request.id, principal), t("ws.assignedToast"), undefined, {
      label: t("toast.undo"),
      run: async () => {
        await act(cockpit.removeRequestAssignee(request.id, principal), t("toast.undoneAssignment"));
      },
    });
  };
  const removeAssignee = (principal: AssigneeRef) => {
    if (!request) return;
    void act(cockpit.removeRequestAssignee(request.id, principal), t("ws.unassigned"), undefined, {
      label: t("toast.undo"),
      run: async () => {
        addingSelf(principal);
        await act(cockpit.addRequestAssignee(request.id, principal), t("toast.undoneAssignment"));
      },
    });
  };

  return {
    assignees,
    closeOpen,
    closeReason,
    closeBusy,
    closeError,
    setCloseOpen,
    setCloseReason,
    setCloseError,
    openMarkDone,
    submitMarkDone,
    trashOpen,
    trashBusy,
    trashError,
    setTrashOpen,
    openTrash,
    submitTrash,
    restoreFromTrash,
    addAssignee,
    removeAssignee,
  };
}
